import { useVip } from './VipContext'
import { usePlayerAccess } from './PlayerAccess'
import { plainText } from '../plain-text'
import { UiIcon } from './UiIcon'
import { useEffect, useState } from 'react'
import { content, attributeKeys, type AttributeKey, type Choice } from '../content-adapter'
import { choiceDeath, historicalChoice, currentNode, getDeathType, getReviveStatus, type GameState } from '../game'
import { PagedText } from './PagedText'

function pageResetKey(state: GameState, suffix: string): string {
  return `${state.nodeIdx}:${state.branchId || 'main'}:${state.phase}:${state.fateId || 'none'}:${suffix}`
}

export function ChoiceList({ state, choices, onChoose, disabled = false, fixedIndex }: { state: GameState; choices: Choice[]; onChoose: (originalIndex: number) => void; disabled?: boolean; fixedIndex?: number }) {
  const vip = useVip()
  const historical = historicalChoice(state)
  const node = currentNode(state)

  return (
    <div className="choice-area" data-choice-count={choices.length}>
      <div className="choice-heading"><span>你会怎么选？</span><small>{node?.id === 'N0' ? '每个片段选择一次' : '选择会改变属性与后续命运'}</small></div>
      <div className="choice-viewport" aria-live="polite" aria-label="全部选项">
        <div className="choice-list">
          {choices.map((choice, visibleIndex) => {
            const originalIndex = node?.choices.indexOf(choice) ?? -1
            if (originalIndex < 0) return null
            const key = `${node?.id || 'node'}-${originalIndex}`
            const deadly = vip.vipStatus === 'active' && !state.branchId && node?.id !== 'N0' ? choiceDeath(state, originalIndex) : null
            const highlight = vip.vipStatus === 'active' && historical === originalIndex && !deadly
            const hint = deadly ? `死路预警：${plainText(deadly.text)}` : highlight ? node?.histBadge === '风格' ? '最贴合孙割风格' : '孙割当年，就是这么选的' : undefined
            return <button type="button" disabled={disabled} title={hint} className={`choice-card${deadly ? ' vip-deadly' : highlight ? ' vip-hl' : ''}${fixedIndex === originalIndex ? ' svip-fixed' : ''}`} data-choice-tag={choice.tag} key={key} data-original-index={originalIndex} onClick={() => onChoose(originalIndex)}><span className="choice-index">{String.fromCharCode(65 + visibleIndex)}</span><span className="choice-copy">{plainText(choice.text)}</span><span className="choice-arrow" aria-hidden="true"><UiIcon name="next" /></span></button>
          })}
        </div>
      </div>
    </div>
  )
}

export function ResultPanel({ state, onContinue }: { state: GameState; onContinue: () => void }) {
  const pending = state.pendingResult
  const node = currentNode(state)
  const selected = node?.choices[pending?.choiceIdx ?? -1]
  return <div className="state-panel result-panel">
    <div className="result-scroll" tabIndex={0} role="region" aria-label="选择结果全文">
      <span className="panel-kicker"><UiIcon name="✦" /> 选择的回响</span>
      <h2 id="game-title">{plainText(node?.title || '人生继续')}</h2>
      {node?.scene && <p className="result-context">{plainText(node.scene)}</p>}
      {selected && <div className="result-selection">你选择了：{plainText(selected.text)}</div>}
      <p className="state-body-text">{plainText(pending?.result || '')}</p>
    </div>
    <div className="result-actions">
      {pending?.delta && Object.values(pending.delta).some(value => value !== 0) && <div className="delta-line delta-icons">{[...attributeKeys, 'black', 'risk', 'rep', 'clean'].map(key => {
        const value = pending.delta?.[key as keyof typeof pending.delta]
        if (!value) return null
        const label = content.attrNames[key as AttributeKey] || ({ black: '黑料', risk: '风险', rep: '声望', clean: '清白' } as Record<string, string>)[key]
        const icon = content.attrIcons[key as AttributeKey] || ({ black: '⚠', risk: '🔥', rep: '📢', clean: '✨' } as Record<string, string>)[key]
        return <span className="delta-item" key={key}><UiIcon name={icon} />{label} {value > 0 ? '+' : ''}{value}</span>
      })}</div>}
      <button type="button" className="primary-button full" onClick={onContinue}>继续人生 <UiIcon name="next" /></button>
    </div>
  </div>
}

export function DeathPanel({ state, error, onRevive, onRestart }: { state: GameState; error: string; onRevive: () => boolean; onRestart: () => void }) {
  const { nickname, requestRevive } = usePlayerAccess()
  const [adPhase, setAdPhase] = useState<'closed' | 'watching' | 'ready'>('closed')
  const [adSeconds, setAdSeconds] = useState(5)
  const [adFeedback, setAdFeedback] = useState('')
  const death = getDeathType(state.deathInfo?.type)
  const quota = getReviveStatus(state)
  useEffect(() => {
    if (adPhase !== 'watching') return
    const timer = window.setInterval(() => {
      setAdSeconds(current => {
        if (current <= 1) {
          setAdPhase('ready')
          return 0
        }
        return current - 1
      })
    }, 1000)
    return () => window.clearInterval(timer)
  }, [adPhase])

  const startAd = () => {
    if (!requestRevive()) return
    if (quota.remaining <= 0) {
      setAdFeedback(`今日看广告复活额度已耗尽（${quota.today}），未播放广告，明天自动恢复 3 次。`)
      return
    }
    setAdFeedback('')
    setAdSeconds(5)
    setAdPhase('watching')
  }
  const cancelAd = () => {
    setAdPhase('closed')
    setAdFeedback('已取消观看广告，未消耗复活额度，仍停留在死亡页。')
  }
  const completeAd = () => {
    if (adPhase !== 'ready') return
    if (!onRevive()) {
      const latest = getReviveStatus(state)
      setAdFeedback(latest.remaining <= 0 ? `今日看广告复活额度已耗尽（${latest.today}），本次未消耗额度。` : '复活失败：当前存档状态已变化，请重新打开死亡页。')
      return
    }
    setAdPhase('closed')
    setAdFeedback('广告播放完成，复活成功，已恢复受损属性。')
  }
  if (adPhase !== 'closed') {
    const ad = content.fakeAds[Math.min(state.revives, content.fakeAds.length - 1)]
    return <div className="state-panel death-panel"><span className="panel-kicker"><UiIcon name="📺" /> 正在播放模拟广告</span><h3 id="game-title">{ad?.title || '孙割特别广告'}</h3><PagedText className="panel-paged-text" textClassName="state-body-text" text={ad?.text || '广告播放中，请稍候。'} resetKey={pageResetKey(state, `ad:${state.revives}:${adPhase}`)} label="模拟广告" /><div className="cloud-status-card"><strong>{adPhase === 'watching' ? `${adSeconds} 秒后可完成广告` : '广告已播放完成'}</strong><small>{adPhase === 'watching' ? '倒计时结束前不会调用复活，也不会消耗额度。' : '点击完成后才会调用真实复活逻辑。'}</small></div>{adPhase === 'watching' ? <button type="button" className="secondary-button full" onClick={cancelAd}>取消/关闭广告</button> : <button type="button" className="primary-button full" onClick={completeAd}>完成广告并复活</button>}<p className="manual-note">关闭或取消不会消耗今日额度。</p></div>
  }
  return <div className="state-panel death-panel"><span className="death-icon" aria-hidden="true"><UiIcon name={death.icon} /></span><span className="panel-kicker">人生提前结束</span><h3 id="game-title">{plainText(death.name)}</h3><p className="death-subtitle">{nickname ? `${nickname}，${plainText(death.title)}` : plainText(death.title)}</p><PagedText className="panel-paged-text" textClassName="state-body-text" text={state.deathInfo?.text || ''} resetKey={pageResetKey(state, `death:${state.deathInfo?.type || 'none'}`)} label="死亡说明" /><button type="button" className="primary-button full" onClick={startAd} disabled={quota.remaining <= 0}><UiIcon name="📺" /> 看广告复活（今日剩余 {quota.remaining} 次）</button><small className="manual-note">{quota.resets ? `检测到新日期，将重置为 3 次（${quota.today}）。` : `今日额度：${quota.remaining}/3（${quota.date || quota.today}）。`} {quota.remaining <= 0 ? '今日额度已耗尽，明天自动恢复。' : '完成广告后才会消耗额度并复活。'}</small>{(error || adFeedback) && <p className="inline-error" role="alert">{adFeedback || error}</p>}<button type="button" className="secondary-button full" onClick={onRestart}>重开人生</button></div>
}

export function FatePanel({ state, fate, onChoose, onEnterBranch }: { state: GameState; fate: (typeof content.fateLines)[number]; onChoose: (variant: 'high' | 'cost') => void; onEnterBranch: () => void }) {
  return <div className="state-panel fate-panel"><span className="fate-icon" aria-hidden="true"><UiIcon name={fate.icon} /></span><span className="panel-kicker">命运在敲门</span><h3 id="game-title">{plainText(fate.name)}</h3><PagedText className="panel-paged-text" textClassName="state-body-text" text={fate.story || fate.knock || ''} resetKey={pageResetKey(state, `fate:${fate.id}`)} label="命运说明" />{fate.nodes?.length ? <><p className="manual-note">这条命运线有 {fate.nodes.length} 个支线节点，先进入支线；完成最后一个节点时再选择高光或代价。</p><button type="button" className="primary-button full" onClick={onEnterBranch}>进入命运线</button></> : <div className="fate-choice-grid"><button type="button" className="fate-choice high" onClick={() => onChoose('high')}><strong>{fate.high.name}</strong><span>{fate.high.text}</span></button><button type="button" className="fate-choice cost" onClick={() => onChoose('cost')}><strong>{fate.cost.name}</strong><span>{fate.cost.text}</span></button></div>}</div>
}

export function EndingPanel({ state, onRestart }: { state: GameState; onRestart: () => void }) {
  return <div className="state-panel ending-panel"><span className="ending-icon" aria-hidden="true"><UiIcon name={state.endingInfo?.icon || "🏁"} /></span><span className="panel-kicker">这一页，暂时写到这里</span><h3 id="game-title">{plainText(state.endingInfo?.title || '人生结局')}</h3><PagedText className="panel-paged-text" textClassName="state-body-text" text={state.endingInfo?.text || ''} resetKey={pageResetKey(state, `ending:${state.endingInfo?.id || 'none'}`)} label="结局正文" /><div className="ending-stats"><span>结局收藏 {state.endingsCollected.length}</span><span>命运线 {state.fateCollected.length}</span><span>复活 {state.revives} 次</span></div><button type="button" className="primary-button full" onClick={onRestart}>再活一次 <span aria-hidden="true"><UiIcon name="next" /></span></button></div>
}
